/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { useState, useEffect, Fragment, useRef } from 'react';
import classNames from 'classnames/bind';
import Checkbox from '../Checkbox/Checkbox';
import { KeyConstants, Theme, MLPVersion } from '../types/enums';
import { FAIcon, IconStyle } from '../Icons/Icons';
import { IconButton } from '../IconButton';
import { SkeletonLoader } from '../SkeletonLoader';
import { RadioButton } from '../RadioButton';
import { SortOrders } from './Table';
import type { TableHeaderProps, TableBodyProps, DynamicObjectInterface, ColumnsInterface } from './types/propType';
import { areAllChildrenChecked, areAnyChildrenChecked, getCheckboxStates, handleSorting } from './tableUtils';
import { TableVersion } from './types/enums';

export const TableHeader = ({
  columns,
  tableData,
  rowKey,
  getWidth,
  setTableData,
  setSelectedKeys,
  selectedKeys = [],
  sortingData,
  setSortingData,
  theme = Theme.GRAY,
  sortingCallback,
  paginationData,
  styles = {},
  enableCheckbox,
  enableRadioButton,
  defaultCheckedAllRows = true,
  disabledItems = [],
  disabledCheckboxes = [],
  isServerSideFetch = false,
  pagination,
  adminSortingCallback,
  enableStickyHeader,
  enableSlider = false,
  slideBack,
  slideForward,
}: TableHeaderProps) => {
  const { recordStartIndex, recordEndIndex } = paginationData;
  const [headerCheckBox, setHeaderCheckBox] = useState(false);
  const [partialHeaderCheckBox, setPartialHeaderCheckBox] = useState(false);
  const cx = classNames.bind(styles);

  useEffect(() => {
    if (!rowKey) {
      return;
    }
    const newTableData =
      pagination && !isServerSideFetch
        ? tableData.slice(Number(recordStartIndex), Number(recordEndIndex))
        : tableData.slice();
    const { allChecked, anyChecked } = getCheckboxStates(
      newTableData,
      rowKey,
      [...disabledItems, ...disabledCheckboxes],
      selectedKeys,
    );
    setHeaderCheckBox(allChecked);

    setPartialHeaderCheckBox(allChecked ? false : anyChecked);
  }, [recordStartIndex, recordEndIndex, tableData, selectedKeys]);

  useEffect(() => {
    if (defaultCheckedAllRows && enableCheckbox) {
      handleHeaderCheckbox();
    }
  }, [enableCheckbox, defaultCheckedAllRows]);

  const isRowInCurrentPage = (index: number): boolean => {
    if (pagination && !isServerSideFetch) {
      return index >= recordStartIndex && index < recordEndIndex;
    }
    return true;
  };

  const handleHeaderCheckbox = () => {
    const newTable =
      pagination && !isServerSideFetch
        ? tableData.slice(Number(recordStartIndex), Number(recordEndIndex))
        : tableData.slice();

    const checkedValue = !headerCheckBox;
    const selectedItems: string[] = [];
    const disabled = [...disabledItems, ...disabledCheckboxes];
    if (defaultCheckedAllRows) {
      selectedItems.push(...disabledCheckboxes);
    }
    if (checkedValue) {
      newTable.forEach((row, index) => {
        if (rowKey && !disabled.includes(row[rowKey]) && isRowInCurrentPage(index)) {
          selectedItems.push(row[rowKey]);
          if (Array.isArray(row.children)) {
            const childKeys = row.children
              .filter((child: DynamicObjectInterface) => !disabled.includes(child[rowKey]))
              .map((child: DynamicObjectInterface) => child[rowKey]);
            selectedItems.push(...childKeys);
          }
        }
      });
    }

    setSelectedKeys(selectedItems);
    setHeaderCheckBox(checkedValue);
    setTableData(newTable);
  };

  const handleTableSort = (sortable: boolean | undefined, key: string) => {
    if (sortable) {
      const sortOrder = sortingData[key] && sortingData[key] === SortOrders.ASC ? SortOrders.DESC : SortOrders.ASC;
      setSortingData({ [key]: sortOrder });
      if (adminSortingCallback) {
        adminSortingCallback(key, sortOrder, paginationData.currentPage);
      } else if (sortingCallback) {
        const sortedData = sortingCallback(key, sortOrder, paginationData.currentPage);
        if (sortedData) {
          setTableData(sortedData);
        }
      } else {
        setTableData(handleSorting({ [key]: sortOrder }, tableData));
      }
    }
  };
  const CTHeadClassname = cx({ 'enable-sticky-header': enableStickyHeader }, theme);

  return (
    <thead id={'table-header'} data-testid={'table-thead-testid'} className={CTHeadClassname}>
      <tr>
        {columns.map(({ key, text, width, sortable }, index) => {
          let icon = sortable ? (
            <FAIcon className={styles['icon']} iconName={'sort'} iconStyle={IconStyle.SOLID} />
          ) : (
            ''
          );
          if (sortable && sortingData[key] === SortOrders.ASC) {
            icon = <FAIcon className={styles['icon']} iconName={'sort-up'} iconStyle={IconStyle.SOLID} />;
          } else if (sortingData[key] === SortOrders.DESC) {
            icon = <FAIcon className={styles['icon']} iconName={'sort-down'} iconStyle={IconStyle.SOLID} />;
          }

          return (
            <Fragment key={key}>
              {enableCheckbox && index === 0 && (
                <th>
                  <Checkbox
                    text=""
                    onChange={handleHeaderCheckbox}
                    checked={headerCheckBox}
                    indeterminate={partialHeaderCheckBox}
                    theme={Theme.WHITE}
                    data-testid="table-header-checkbox-testid"
                  />
                </th>
              )}
              {enableRadioButton && index === 0 && <th data-testid="table-header-radiobutton-testid"></th>}
              {enableSlider && slideBack && index === 0 ? (
                <th className={styles['slider-icon']}>
                  <IconButton
                    iconName={'chevron-left'}
                    data-testid="slider-icon"
                    onClick={slideBack}
                    iconStyle={IconStyle.SOLID}
                  />
                </th>
              ) : null}
              <th
                style={getWidth(width)}
                className={sortable ? styles['sortable-header'] : ''}
                onClick={() => {
                  handleTableSort(sortable, key);
                }}
              >
                {text}
                {icon}
              </th>
            </Fragment>
          );
        })}
        {enableSlider && slideForward ? (
          <th className={styles['slider-icon']}>
            <IconButton
              iconName={'chevron-right'}
              data-testid="slider-icon"
              onClick={slideForward}
              iconStyle={IconStyle.SOLID}
            />
          </th>
        ) : null}
      </tr>
    </thead>
  );
};

export const TableBody = ({
  columns,
  rowExpandCallback,
  tableData,
  rowKey: propRowKey,
  onRowClick,
  selected,
  setSelectedKeys,
  selectedKeys = [],
  pagination,
  paginationData,
  getWidth,
  styles = {},
  isServerSideFetch = false,
  topAlignedDataInRow = false,
  enableCheckbox,
  enableRadioButton,
  disabledItems = [],
  disabledCheckboxes = [],
  disabledRadioButtons = [],
  columnsWithOverflowVisible = [],
  tableVersion,
  mlpVersion,
  shouldFocusRow = false,
  tableFooter,
  enableSlider,
  isLoading = false,
}: TableBodyProps) => {
  const [updatedSelectedKeys, setUpdatedSelectedKeys] = useState<string[]>(selectedKeys);
  const { recordStartIndex, recordEndIndex } = paginationData;
  const rowKey = propRowKey as string;
  const cx = classNames.bind(styles);
  const keys: string[] = [KeyConstants.ENTER, KeyConstants.SPACE];
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  const tableBodyRef = useRef<HTMLTableSectionElement | null>(null);

  useEffect(() => {
    if (selectedKeys) {
      setUpdatedSelectedKeys(selectedKeys);
    }
  }, [selectedKeys]);

  const handleRowCheckboxToggle = (rowData: DynamicObjectInterface, childRowData?: DynamicObjectInterface) => {
    if (!setSelectedKeys) {
      return;
    }

    const parentIndex = tableData.findIndex((row) => row[rowKey] === rowData[rowKey]);
    const parentRow = tableData[parentIndex];
    const originalChildren = parentRow.children || [];

    let newSelectedKeys = [...updatedSelectedKeys];

    if (childRowData) {
      const childKey = childRowData[rowKey];
      const isChecked = newSelectedKeys.includes(childKey);
      if (isChecked) {
        newSelectedKeys = newSelectedKeys.filter((key) => key !== childKey);
        newSelectedKeys = newSelectedKeys.filter((key) => key !== rowData[rowKey]);
      } else {
        newSelectedKeys.push(childKey);
        const isAllChecked = areAllChildrenChecked(originalChildren, rowKey, [...disabledItems], newSelectedKeys);
        if (isAllChecked) {
          newSelectedKeys.push(rowData[rowKey]);
        }
      }
    } else {
      const parentKey = rowData[rowKey];
      const isChecked = newSelectedKeys.includes(parentKey);
      if (isChecked) {
        newSelectedKeys = newSelectedKeys.filter((key) => key !== parentKey);
        originalChildren.map((child: DynamicObjectInterface) => {
          newSelectedKeys = newSelectedKeys.filter((key) => key !== child[rowKey]);
        });
      } else {
        newSelectedKeys.push(parentKey);
        originalChildren.map((child: DynamicObjectInterface) => {
          newSelectedKeys.push(child[rowKey]);
        });
      }
    }

    setSelectedKeys(newSelectedKeys);
  };

  const handleRowRadioToggle = (rowData: DynamicObjectInterface) => {
    if (!setSelectedKeys) {
      return;
    }
    const rowId = rowData[rowKey];
    setSelectedKeys([rowId]);
  };

  const isSelectedRow = (
    enableCheckbox: boolean | undefined,
    enableRadioButton: boolean | undefined,
    rowData: DynamicObjectInterface,
    selected: string | undefined,
    rowKey: string,
  ) =>
    enableCheckbox || enableRadioButton ? updatedSelectedKeys?.includes(rowData[rowKey]) : selected === rowData[rowKey];

  const checkboxTheme = (rowData: DynamicObjectInterface) =>
    updatedSelectedKeys?.includes(rowData[rowKey]) ? Theme.BLUE : Theme.BLACK;

  const renderChildRows = (rowData: DynamicObjectInterface, rowIndex: number, isDisabled: boolean) => {
    if (!rowData.children || !rowData.isExpanded) {
      return null;
    }
    return rowData.children.map((childData: DynamicObjectInterface, childIndex: number) => {
      const childKey = rowKey ? `${childData[rowKey]}-${childIndex}` : `${rowIndex}-${childIndex}`;
      const isLastChild = childIndex === rowData.children.length - 1;
      const shouldRenderSpacer = childIndex > 0;
      const CSpacerRowCls = cx('children-row', 'space-row');

      const CBodyClassname = cx('children-row', {
        'disabled-row': isDisabled,
        'last-child-of-parent': isLastChild,
      });

      return (
        <Fragment key={childKey}>
          {shouldRenderSpacer && (
            <tr className={CSpacerRowCls} data-group={`${rowIndex}`}>
              <td colSpan={columns.length + 1}></td>
            </tr>
          )}
          <tr className={CBodyClassname} data-testid="tbody-tr-child-testid" data-group={rowIndex}>
            {enableCheckbox ? <td /> : ''}
            {(() => {
              const cells: JSX.Element[] = [];
              let i = 0;

              while (i < columns.length) {
                const col = columns[i] as ColumnsInterface;

                const colSpanValue =
                  childData?.colSpan?.[col.key] && typeof childData.colSpan[col.key] === 'number'
                    ? childData.colSpan[col.key]
                    : 1;

                const cellContent =
                  enableCheckbox && i === 0 ? (
                    <div className={styles['child-nested-spacing']}>
                      <div className={styles['row-checkbox']}>
                        <Checkbox
                          text=""
                          disabled={isDisabled || disabledCheckboxes.includes(childData[rowKey])}
                          onChange={() => handleRowCheckboxToggle(rowData, childData)}
                          checked={updatedSelectedKeys?.includes(childData[rowKey])}
                          theme={checkboxTheme(childData)}
                          data-testid="child-row-checkbox-testid"
                          className={styles['clickable']}
                        />
                      </div>
                      <div>{col.render(childData, childIndex)}</div>
                    </div>
                  ) : (
                    col.render(childData, childIndex)
                  );

                cells.push(
                  <td key={col.key} colSpan={colSpanValue} style={{ ...getWidth(col.width) }}>
                    {cellContent}
                  </td>,
                );

                // Skip the column(s) covered by this colSpan
                i += colSpanValue;
              }

              return cells;
            })()}
          </tr>
        </Fragment>
      );
    });
  };

  const renderLoadingState = (columnCount: number, rowCount: number = 20) => {
    const CSkeletonRowCls = cx('skeleton-row');

    return Array.from({ length: rowCount }, (_, index) => (
      <tr key={`loading-row-${index}`} className={CSkeletonRowCls} data-testid="tbody-loading-tr-testid">
        <td colSpan={columnCount}>
          <SkeletonLoader height="16" borderRadius="6" />
        </td>
      </tr>
    ));
  };

  const getIndeterminateState = (row: DynamicObjectInterface) => {
    const all = areAllChildrenChecked(row.children || [], rowKey, [...disabledItems], updatedSelectedKeys);
    const some = areAnyChildrenChecked(row.children || [], rowKey, [...disabledItems], updatedSelectedKeys);
    return all ? false : some;
  };

  const shouldToggleOnKeyDown = (
    event: React.KeyboardEvent<HTMLTableRowElement>,
    rowData: DynamicObjectInterface,
    rowIndex: number,
  ) => {
    if (keys.includes(event.code)) {
      onRowClick?.(rowData, rowIndex);
      handleRowCheckboxToggle(rowData);
    }
    if (event.key === KeyConstants.ARROW_DOWN) {
      const nextIndex = Math.min(rowIndex + 1, tableData.length - 1);
      rowRefs.current[nextIndex]?.focus();
    }
    if (event.key === KeyConstants.ARROW_UP) {
      const prevIndex = Math.max(rowIndex - 1, 0);
      rowRefs.current[prevIndex]?.focus();
    }
  };
  const CTBodyClassname = cx({
    'top-aligned-content': topAlignedDataInRow,
  });
  const CBodyFooterRowClassname = cx('table-footer-row', 'row-compact');
  const dataToRender =
    pagination && !isServerSideFetch ? tableData.slice(Number(recordStartIndex), Number(recordEndIndex)) : tableData;
  const showSkeleton = tableVersion === TableVersion.ADMIN && isLoading;

  return (
    <tbody id={'table-body'} ref={tableBodyRef} data-testid="table-tbody-testid" className={CTBodyClassname}>
      {showSkeleton && renderLoadingState(columns.length)}
      {!showSkeleton &&
        dataToRender.map((rowData: DynamicObjectInterface, rowIndex: number) => {
          const rowId = rowData[rowKey];
          const rowKeyId = rowKey ? `${rowId}-${rowIndex}` : `${rowIndex}`;
          const isDisabled = disabledItems?.includes(rowId);
          const CSpacerRowCls = cx('space-row');
          const CBodyClassname = cx({
            'selected-row': rowKey && isSelectedRow(enableCheckbox, enableRadioButton, rowData, selected, rowKey),
            'disabled-row': isDisabled,
            'row-highlight': rowData.isHighlighted,
            'has-children': rowData.children && rowData.isExpanded,
            clickable: onRowClick,
          });

          return (
            <Fragment key={rowKeyId}>
              {tableVersion === TableVersion.ADMIN && mlpVersion !== MLPVersion.THREE ? (
                <tr className={CSpacerRowCls} />
              ) : (
                ''
              )}
              <tr
                ref={(element) => (rowRefs.current[rowIndex] = element)}
                tabIndex={shouldFocusRow ? 0 : undefined}
                className={CBodyClassname}
                data-testid={'tbody-tr-testid'}
                onKeyDown={(event) => shouldToggleOnKeyDown(event, rowData, rowIndex)}
              >
                {columns.map((columnObj, colIndex) => {
                  const cellKey = `${columnObj.key}-${rowKeyId}`;
                  const showCheckbox = enableCheckbox && colIndex === 0;
                  const showRadiobutton = enableRadioButton && colIndex === 0;
                  const showEmpty = enableSlider && colIndex === 0;
                  const style = {
                    ...getWidth(columnObj.width),
                    ...(columnsWithOverflowVisible.includes(columnObj.key) && { overflow: 'visible' }),
                  };
                  const tableDataContainer = cx({
                    'show-expand-icon': rowData.showExpandIconColKey?.includes(columnObj.key) && rowData.children,
                  });
                  return (
                    <Fragment key={cellKey}>
                      {showCheckbox && (
                        <td className={styles['row-checkbox']}>
                          <Checkbox
                            text=""
                            disabled={isDisabled || disabledCheckboxes.includes(rowData[rowKey])}
                            onChange={() => handleRowCheckboxToggle(rowData)}
                            checked={updatedSelectedKeys.includes(rowData[rowKey])}
                            indeterminate={getIndeterminateState(rowData)}
                            theme={checkboxTheme(rowData)}
                            data-testid="row-checkbox-testid"
                            className={styles['clickable']}
                          />
                        </td>
                      )}
                      {showRadiobutton && (
                        <td className={styles['row-radioButton']}>
                          <RadioButton
                            disabled={isDisabled || disabledRadioButtons.includes(rowData[rowKey])}
                            onChange={() => handleRowRadioToggle(rowData)}
                            checked={updatedSelectedKeys.includes(rowData[rowKey])}
                            data-testid="row-radiobutton-testid"
                            className={styles['clickable']}
                          />
                        </td>
                      )}
                      {showEmpty && <td className={styles['row-checkbox']}> </td>}
                      <td
                        style={style}
                        onClick={() => onRowClick?.(rowData, rowIndex)}
                        onKeyDown={(e) => e.key === 'Enter' && onRowClick?.(rowData, rowIndex)}
                      >
                        <div
                          role="button"
                          aria-label="Expand Row"
                          tabIndex={0}
                          className={tableDataContainer}
                          data-testid="table-data-item-testid"
                          onClick={() => {
                            if (rowExpandCallback) {
                              rowExpandCallback(rowData, rowIndex);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (rowExpandCallback && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              rowExpandCallback(rowData, rowIndex);
                            }
                          }}
                        >
                          {columnObj.render(rowData, rowIndex)}
                          {rowData.showExpandIconColKey?.includes(columnObj.key) && rowData.children && (
                            <FAIcon
                              iconName="chevron-down"
                              className={styles['expand-icon']}
                              data-testid="expand-icon-testid"
                            />
                          )}
                        </div>
                      </td>
                    </Fragment>
                  );
                })}
                {enableSlider && <td className={styles['row-checkbox']}> </td>}
              </tr>
              {renderChildRows(rowData, rowIndex, isDisabled)}
            </Fragment>
          );
        })}
      {tableFooter && (
        <tr className={CBodyFooterRowClassname} data-testid="tbody-footer-tr-child-testid">
          {columns.map((childCol, colIndex) => {
            const cells: JSX.Element[] = [];
            const showSelectionColumn = (enableCheckbox || enableRadioButton) && colIndex === 0;
            cells.push(
              <td key={childCol.key} colSpan={showSelectionColumn ? 2 : 0} style={getWidth(childCol.width)}>
                <>{childCol.render(tableFooter, 0)}</>
              </td>,
            );

            return cells;
          })}
        </tr>
      )}
    </tbody>
  );
};
